import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function AboutPage() {
  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-[72px]">
        <section className="w-full py-16 md:py-24 bg-background-50">
          <div className="px-4 md:px-6 max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8">
              <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700">About</span>
            </nav>

            <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground-950 mb-6">
              About Kakobuy Finds
            </h1>

            <div className="prose-custom space-y-6">
              <p className="text-lg text-foreground-600 leading-relaxed">
                Our mission is to create a reliable educational resource helping users understand product spreadsheets, shopping research, and online purchasing methods.
              </p>

              <h2 className="text-2xl font-heading font-semibold text-foreground-950 mt-10 mb-4">What We Do</h2>
              <p className="text-base text-foreground-600 leading-relaxed">
                Kakobuy Finds serves as an independent information hub dedicated to helping shoppers navigate the world of product spreadsheets and organized product discovery. We research, document, and explain how these tools work so that both beginners and experienced shoppers can make more informed decisions.
              </p>

              <h2 className="text-2xl font-heading font-semibold text-foreground-950 mt-10 mb-4">Our Focus Areas</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                {[
                  { icon: 'ri-search-eye-line', title: 'Research', desc: 'Thorough investigation of product discovery methods and spreadsheet tools' },
                  { icon: 'ri-check-double-line', title: 'Content Accuracy', desc: 'Commitment to providing verified, up-to-date information' },
                  { icon: 'ri-user-heart-line', title: 'User Education', desc: 'Clear, accessible guides for shoppers at every experience level' },
                  { icon: 'ri-shield-star-line', title: 'Safety Information', desc: 'Practical guidance on safe shopping practices and verification' },
                ].map((item) => (
                  <div key={item.title} className="flex gap-3 p-4 bg-background-100 rounded-lg border border-background-200/70">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center text-lg text-primary-500">
                      <i className={item.icon}></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">{item.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <h2 className="text-2xl font-heading font-semibold text-foreground-950 mt-10 mb-4">Important Notice</h2>
              <p className="text-base text-foreground-600 leading-relaxed">
                We do not guarantee product availability or prices. The information provided on this website is for educational and informational purposes only. Users should always verify product details, pricing, and availability directly with sellers before making purchasing decisions.
              </p>

              <div className="mt-10 p-6 bg-accent-50 rounded-lg border border-accent-200/50">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center text-accent-600 mt-0.5">
                    <i className="ri-information-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-accent-800 mb-2">Disclaimer</h4>
                    <p className="text-sm text-accent-700 leading-relaxed">
                      Kakobuy Finds is an independent educational resource. We are not affiliated with or endorsed by any shopping platform. Product links direct to third-party websites, and we may not have control over their content or availability. Always exercise due diligence when shopping online.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-background-200/70">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to Homepage
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-16 bg-background-100">
          <div className="px-4 md:px-6 max-w-3xl mx-auto">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-8">Written By</h2>
            <div className="bg-background-50 rounded-lg border border-background-200/70 p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center text-2xl text-primary-600">
                  <i className="ri-team-line"></i>
                </div>
                <div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-900">Kakobuy Finds Research Team</h3>
                  <p className="text-sm text-foreground-500">Shopping Research &amp; Product Analysis</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                <div className="text-sm text-foreground-600">
                  <strong className="text-foreground-800">Expertise:</strong> Shopping research, spreadsheet analysis, product comparison
                </div>
                <div className="text-sm text-foreground-600">
                  <strong className="text-foreground-800">Experience:</strong> Years researching shopping platforms, product databases and online purchasing methods
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: 'About Kakobuy Finds',
            description: 'Educational resource for Kakobuy spreadsheets and product research.',
            url: 'https://kakobuyfinds.bond/about',
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://kakobuyfinds.bond/' },
              { '@type': 'ListItem', position: 2, name: 'About', item: 'https://kakobuyfinds.bond/about' },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Kakobuy Finds Research Team',
            url: 'https://kakobuyfinds.bond/about',
            description: 'Independent research team specializing in shopping research, spreadsheet analysis, and product comparison.',
            foundingDate: '2026',
          }),
        }}
      />
    </>
  );
}